import React, { FunctionComponent, MouseEvent } from "react";

import type { InvalidQuestionWarning } from ".";
import { AnswerTemplate } from "../../db/models/Answer";
import { QuestionGroup as FormQuestionGroup } from "../../db/models/Form";
import { useTranslate } from "../translation/frontend";
import ErrorSummary from "./ErrorSummary";
import { PaginationWide } from "./Pagination";
import QuestionComponent from "./Question";
import ArrowRightIcon from "./icons/ArrowRightIcon";

interface QuestionGroupProps {
  page: FormQuestionGroup;
  answers: Record<string, AnswerTemplate[]>;
  onBack?: (ev: MouseEvent<HTMLButtonElement>) => void;
  onSkipToEnd?: (ev: MouseEvent<HTMLButtonElement>) => void;
  isLastPage: boolean;
  loading: boolean;
  showValidationError: boolean;
  onInvalidQuestion: (warning: InvalidQuestionWarning) => void;
  onValidQuestion: (warning: InvalidQuestionWarning) => void;
  invalidQuestions: InvalidQuestionWarning[];
}

const QuestionGroup: FunctionComponent<QuestionGroupProps> = ({
  page,
  answers,
  onBack,
  onSkipToEnd,
  isLastPage,
  loading,
  showValidationError,
  onInvalidQuestion,
  onValidQuestion,
  invalidQuestions,
}) => {
  const translate = useTranslate();

  return (
    <>
      {page.groupIntro || page.showSkipToEnd ? (
        <section className="form-group-intro section">
          <div className="container--slim">
            <div
              className="content-styles allow-cross-form-links"
              dangerouslySetInnerHTML={{
                __html:
                  page.groupIntro ??
                  "The following questions are optional. Answering these questions will help our team track trends and problems across the institution. Reporting now will submit everything you’ve done so far.",
              }}
            />
          </div>
        </section>
      ) : null}
      {page.showSkipToEnd ? (
        <section className="form-group-intro form-group-intro__skip-to-end section">
          <div className="container--slim container--slim__skip-to-end">
            <button
              className="btn btn--skip-to-end btn--swipe btn--arrow"
              name="_action"
              value="skip-to-end"
              onClick={onSkipToEnd}
              formNoValidate
              disabled={loading}
            >
              <span>{translate("skipToEndLabel")}</span>
              <span className="btn__arrow">
                <ArrowRightIcon />
              </span>
            </button>
          </div>
        </section>
      ) : null}
      <section className="form-group section">
        <div className="container--slim">
          {invalidQuestions.length ? (
            <ErrorSummary questions={page.questions} warnings={invalidQuestions} answers={answers} />
          ) : null}
          {page.questions.map((question) => (
            <QuestionComponent
              key={question.id}
              question={question}
              answers={answers}
              onInvalid={onInvalidQuestion}
              onValid={onValidQuestion}
            />
          ))}
        </div>
      </section>
      <PaginationWide
        loading={loading}
        showValidationError={showValidationError}
        isLastPage={isLastPage}
        onBack={onBack}
      />
    </>
  );
};

export default QuestionGroup;
